import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_ESTIMATED_DURATION_MS = 30000;
const COMPLETION_ANIMATION_MS = 150;

type UseProgressFacadeProps = {
	/** When true, rapidly animates to 1.0 then fires `onCompleteAnimationFinished`. */
	isComplete: boolean;
	onCompleteAnimationFinished?: () => void;
	/** Defaults to 30000ms. */
	estimatedDurationMs?: number;
};

/**
 * Smooth facade progress value (0-1) for loading bars: animates from 0 to a
 * random 85-95% target over `estimatedDurationMs`, hovers there, then on
 * `isComplete` rapidly fills to 1.0 and fires `onCompleteAnimationFinished`.
 */
export function useProgressFacade({
	isComplete,
	onCompleteAnimationFinished,
	estimatedDurationMs = DEFAULT_ESTIMATED_DURATION_MS,
}: UseProgressFacadeProps): number {
	const [progress, setProgress] = useState(0);

	// Stable target picked once on mount (85-95%).
	const targetRef = useRef(0.85 + Math.random() * 0.1);

	const completionStartValueRef = useRef(0);
	const completionStartTimeRef = useRef<number | null>(null);
	const hasCalledFinishedRef = useRef(false);

	const onCompleteAnimationFinishedRef = useRef(onCompleteAnimationFinished);
	onCompleteAnimationFinishedRef.current = onCompleteAnimationFinished;

	// Phase 1: animate 0 → target.
	useEffect(() => {
		if (isComplete) {
			return;
		}

		let rafId: number;
		let startTime: number | null = null;
		const target = targetRef.current;

		const tick = (timestamp: number) => {
			if (startTime === null) {
				startTime = timestamp;
			}
			const elapsed = timestamp - startTime;
			const linearProgress = Math.min(1, elapsed / estimatedDurationMs);

			setProgress(linearProgress * target);

			if (linearProgress < 1) {
				rafId = requestAnimationFrame(tick);
			}
			// Otherwise we've reached the target — stop and hover.
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(rafId);
		};
	}, [isComplete, estimatedDurationMs]);

	// Phase 2: rapidly animate current value → 1.0.
	const handleCompletion = useCallback(() => {
		completionStartTimeRef.current = null;
		hasCalledFinishedRef.current = false;
		let rafId: number;

		const tick = (timestamp: number) => {
			if (completionStartTimeRef.current === null) {
				completionStartTimeRef.current = timestamp;
			}
			const elapsed = timestamp - completionStartTimeRef.current;
			const t = Math.min(1, elapsed / COMPLETION_ANIMATION_MS);
			const startValue = completionStartValueRef.current;
			const value = startValue + (1 - startValue) * t;

			setProgress(value);

			if (t < 1) {
				rafId = requestAnimationFrame(tick);
			} else if (!hasCalledFinishedRef.current) {
				hasCalledFinishedRef.current = true;
				onCompleteAnimationFinishedRef.current?.();
			}
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(rafId);
		};
	}, []);

	useEffect(() => {
		if (!isComplete) {
			return;
		}

		// Snapshot current progress as the starting point for the completion animation.
		completionStartValueRef.current = progress;

		const cleanup = handleCompletion();
		return cleanup;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger when isComplete changes to true
	}, [isComplete, handleCompletion]);

	return progress;
}
